import { FC, KeyboardEvent, MouseEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Dropdown, MenuProps } from 'antd';

import { selectDictionary } from 'features/dictionaries/dictionariesSelectors';

import { sortStageAndStatusDropdownOptions } from 'utils/sortStageAndStatusDropdownOptions';
import { filterOutInReviewJobStatusOptions } from 'utils/filterJobStatusOptions';

import { JobStatus } from 'constants/enums';

import { Dictionaries } from 'constants/enums';

import { ReactComponent as ArrowDownIcon } from 'assets/icons/arrowDownSmall.svg';
import { ReactComponent as TickSquare } from 'assets/icons/tickSquare.svg';

import styles from './ChangeStatusDropdown.module.scss';

const statusClassNames: Record<string, string> = {
  ACTIVE: styles.active,
  COMPLETED: styles.completed,
  INACTIVE: styles.inactive,
  INCOMPLETE: styles.incomplete
};

const statusIcons: Record<
  string,
  React.FunctionComponent<React.SVGProps<SVGSVGElement>>
> = {
  ACTIVE: TickSquare,
  COMPLETED: TickSquare,
  INACTIVE: TickSquare,
  INCOMPLETE: TickSquare
};

interface ChangeStatusDropdownProps {
  value?: JobStatus;
  dict: string;
  onChange?: (
    value: JobStatus,
    event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>
  ) => void;
  isDisplayIcons?: boolean;
  hasPermission?: boolean;
}

const ChangeStatusDropdown: FC<ChangeStatusDropdownProps> = ({
  value,
  onChange,
  dict,
  isDisplayIcons,
  hasPermission = true
}) => {
  const { t } = useTranslation();
  const dictionarySelector = useMemo(() => selectDictionary(dict), [dict]);
  const options = useSelector(dictionarySelector);

  const filteredOptions = filterOutInReviewJobStatusOptions(options);

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    if (!onChange) {
      return;
    }

    onChange(e.key as JobStatus, e.domEvent);
  };

  const items = sortStageAndStatusDropdownOptions(
    filteredOptions,
    dict as Dictionaries
  ).map((status) => ({
    key: status.value,
    className: statusClassNames[status.value],
    label: status.label,
    icon: statusIcons[status.value]
  }));

  const renderItem = (value?: string) => {
    const option = items.find((item) => item.key === value);

    if (!option) {
      return t('common.selectStatus');
    }

    const isIcon = isDisplayIcons && option.icon;

    return (
      <div className={`${option.className} ${styles.option}`}>
        {isIcon && <option.icon className={styles.statusIcon} />}
        {option.label}
      </div>
    );
  };

  const dropdownItems = items.map((item) => ({
    key: item.key,
    label: renderItem(item.key)
  }));

  return hasPermission ? (
    <Dropdown
      menu={{ items: dropdownItems, onClick: handleMenuClick }}
      rootClassName={styles.dropdownContainer}
    >
      <a
        className={`${styles.dropdownLink} ${
          value ? statusClassNames[value] : ''
        }`}
        onClick={(e) => e.preventDefault()}
      >
        {renderItem(value)}
        <ArrowDownIcon />
      </a>
    </Dropdown>
  ) : (
    <div
      className={`${styles.dropdownLink} ${
        value ? statusClassNames[value] : ''
      }`}
    >
      {renderItem(value)}
    </div>
  );
};

export default ChangeStatusDropdown;
